import { useState } from 'react';
import { CheckCircle2, Circle, Loader2, ChevronDown } from 'lucide-react';
import type { ISetupPlan } from '../../store/useChatStore';

interface SetupPlanProgressProps {
  plan: ISetupPlan;
}

export function SetupPlanProgress({ plan }: SetupPlanProgressProps) {
  const [expanded, setExpanded] = useState(false);
  const { steps, status } = plan;
  const doneCount = steps.filter((s) => s.status === 'done').length;
  const totalCount = steps.length;
  const allDone = doneCount === totalCount;
  const isDraft = status === 'draft';

  // Hide when plan is completed and all steps done
  if (status === 'completed' && allDone) return null;

  const progressPercent = totalCount > 0 ? (doneCount / totalCount) * 100 : 0;
  const currentStep = steps.find((s) => s.status === 'in_progress') || steps.find((s) => s.status === 'pending');

  return (
    <div className="border-b border-border">
      {/* Collapsed header — always visible, tap to toggle */}
      <button
        type="button"
        className="w-full px-4 py-2 min-h-[44px] flex items-center justify-between focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
        aria-expanded={expanded}
        onClick={() => setExpanded((prev) => !prev)}
      >
        <span className="text-xs font-semibold text-foreground truncate">
          {isDraft ? 'Setup Plan (Draft)' : currentStep?.label || 'Setting up...'}
        </span>
        <div className="flex items-center gap-1.5 shrink-0 ml-2">
          {!isDraft && (
            <span className="text-xs text-muted-foreground">
              {doneCount}/{totalCount}
            </span>
          )}
          <ChevronDown
            size={14}
            className={`text-muted-foreground transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}
          />
        </div>
      </button>

      {/* Progress bar — hidden for draft plans */}
      {!isDraft && (
        <div className="px-4 pb-2">
          <div className="w-full h-1.5 rounded-full bg-muted dark:bg-surface-raised overflow-hidden">
            <div
              className="h-full rounded-full bg-primary transition-all duration-500"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>
      )}

      {/* Expanded checklist */}
      {expanded && (
        <div className="px-4 pb-3 space-y-1.5">
          {steps.map((step) => (
            <div key={step.id} className="flex items-center gap-2">
              {step.status === 'done' ? (
                <CheckCircle2 size={16} className="text-emerald-500 shrink-0" aria-hidden="true" />
              ) : step.status === 'in_progress' ? (
                <Loader2 size={16} className="text-primary animate-spin shrink-0" aria-hidden="true" />
              ) : (
                <Circle size={16} className="text-muted-foreground shrink-0" aria-hidden="true" />
              )}
              <span
                className={`text-sm ${
                  step.status === 'done'
                    ? 'text-muted-foreground line-through'
                    : step.status === 'in_progress'
                      ? 'text-foreground font-medium'
                      : 'text-muted-foreground'
                }`}
              >
                {step.label}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
